import { Box } from "@/components/essentials/box";
import { Typography } from "@/components/essentials/typography";
import { Flex } from "@chakra-ui/react";
import Link from "next/link";
import { type FC } from "react";
import {
  specialNoteFormDescriptionTestId,
  specialNoteFormTestId,
  specialNoteFormTextAreaTestId,
  specialNoteFormTextButtonHelpTestId,
  specialNoteFormTitleTestId,
} from "./const";
import { toysubSiblingsPlanUrl } from "@/configs/externalUrls";
import { TextButton } from "@/components/essentials/button";
import { AutoResizeTextarea } from "@/components/essentials/textarea/AutoResizeTextarea";
import { useFormContext } from "react-hook-form";
import { type PlanData } from "@/components/fragments/argyle/plan-data-table";
import { isNonNullable } from "@/modules/utils/predicate";
import { ScheduledVisibilityBox } from "@/components/fragments/box/scheduled-visibility-box/ScheduledVisibilityBox";
import { isFirstSelection } from "@/modules/utils/subscription-info";

export interface SpecialNoteFormProps {
  readonly planData?: PlanData;
}

export const SpecialNoteForm: FC<SpecialNoteFormProps> = ({ planData }) => {
  const { register } = useFormContext();

  return (
    <Flex flexDir="column" data-testid={specialNoteFormTestId}>
      <Box wordBreak="break-word" textAlign="center">
        <Typography variant="display3" data-testid={specialNoteFormTitleTestId}>
          きょうだいプランについて
        </Typography>
        <Typography variant="text4" color="gray.dark" data-testid={specialNoteFormDescriptionTestId}>
          年齢の異なる2名のお子様に3点ずつおもちゃをお送りする「きょうだいプラン」のご提供もございます。
          <br />
          きょうだいプランご希望の場合は、備考欄に対象となるお子様の誕生日、年齢、性別をご明記ください。
          <br />
          3人以上のお子様へのきょうだいプラン適用や、おもちゃ個数配分の変更も可能です。
          <br />
          異なる配分での送付を希望される場合は、各お子様への個数についても記載ください
        </Typography>
        <Box textAlign="right">
          <TextButton
            href={toysubSiblingsPlanUrl}
            as={Link}
            rel="noopener norefferer"
            target="_blank"
            data-testid={specialNoteFormTextButtonHelpTestId}
            color="mono.dark.54"
            mb="1rem"
            size="sm"
          >
            <Typography variant="text4">きょうだいプランの詳細はこちら</Typography>
          </TextButton>
        </Box>
        {isNonNullable(planData) && !isFirstSelection(planData.id) && (
          <ScheduledVisibilityBox startDate="2024-06-19 00:00:00" endDate="2024-06-23 23:59:59">
            <Box mb="2rem">
              <Typography variant="display6" color="ui.red.80">
                ※「おもちゃ1個プラス」キャンペーンについて
              </Typography>
              <Typography variant="text4" color="gray.dark">
                追加されるおもちゃを上のお子さま向けにするか、下のお子さま向けにするか、ご希望がありましたらご入力ください。
                <br />
                ご入力がない場合はおもちゃプランナーが選定させていただきます。
              </Typography>
            </Box>
          </ScheduledVisibilityBox>
        )}

        <AutoResizeTextarea
          mt={2}
          placeholder="例：きょうだいプラン希望、20210101、〇歳、男の子"
          {...register("specialNote")}
          maxLength={1000}
          data-testid={specialNoteFormTextAreaTestId}
        />
      </Box>
    </Flex>
  );
};
